import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { Card, Button, Input, Select, Label, Icon } from '../../components/ui';

const JOURNALS = [
  { value: 'VE', label: 'VE - Ventes' },
  { value: 'AC', label: 'AC - Achats' },
  { value: 'BQ', label: 'BQ - Banque' },
  { value: 'CA', label: 'CA - Caisse' },
  { value: 'OD', label: 'OD - Opérations diverses' },
];

export default function ManualEntry() {
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [journalCode, setJournalCode] = useState('OD');
  const [label, setLabel] = useState('');
  const [lines, setLines] = useState([
    { accountCode: '', accountLabel: '', label: '', debit: '', credit: '', thirdPartyType: 'NONE', thirdPartyId: '' },
    { accountCode: '', accountLabel: '', label: '', debit: '', credit: '', thirdPartyType: 'NONE', thirdPartyId: '' },
  ]);
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAccounts();
    loadClients();
    loadSuppliers();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await api.get('/chart-of-accounts?active=true&limit=500');
      setAccounts(res.data?.accounts || []);
    } catch { setAccounts([]); }
  };

  const loadClients = async () => {
    try {
      const res = await api.get('/clients?limit=500');
      setClients(res.data?.clients || []);
    } catch { setClients([]); }
  };

  const loadSuppliers = async () => {
    try {
      const res = await api.get('/suppliers?limit=500');
      setSuppliers(res.data?.suppliers || []);
    } catch { setSuppliers([]); }
  };

  const addLine = () => {
    setLines([...lines, { accountCode: '', accountLabel: '', label: '', debit: '', credit: '', thirdPartyType: 'NONE', thirdPartyId: '' }]);
  };

  const removeLine = (idx) => {
    if (lines.length <= 2) { toast.warning('Minimum 2 lignes'); return; }
    setLines(lines.filter((_, i) => i !== idx));
  };

  const updateLine = (idx, field, value) => {
    const newLines = [...lines];
    newLines[idx][field] = value;

    if (field === 'accountCode') {
      const acc = accounts.find((a) => a.code === value);
      if (acc) newLines[idx].accountLabel = acc.label;
    }

    setLines(newLines);
  };

  const totals = lines.reduce(
    (acc, l) => ({
      debit: acc.debit + (Number(l.debit) || 0),
      credit: acc.credit + (Number(l.credit) || 0),
    }),
    { debit: 0, credit: 0 }
  );
  const balanced = Math.abs(totals.debit - totals.credit) < 0.001;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!label) { toast.error('Libellé requis'); return; }
    if (!balanced) { toast.error('Écriture non équilibrée'); return; }

    const formattedLines = lines.map((l) => ({
      accountCode: l.accountCode,
      accountLabel: l.accountLabel,
      label: l.label || label,
      debit: Number(l.debit) || 0,
      credit: Number(l.credit) || 0,
      thirdPartyType: l.thirdPartyType || 'NONE',
      thirdPartyId: l.thirdPartyId || null,
    }));

    try {
      setSaving(true);
      await api.post('/accounting/manual-entries', { entryDate, journalCode, label, lines: formattedLines });
      toast.success('Écriture créée avec succès');
      setLines([
        { accountCode: '', accountLabel: '', label: '', debit: '', credit: '', thirdPartyType: 'NONE', thirdPartyId: '' },
        { accountCode: '', accountLabel: '', label: '', debit: '', credit: '', thirdPartyType: 'NONE', thirdPartyId: '' },
      ]);
      setLabel('');
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          <Icon name="IconPencil" className="text-indigo-600 mr-3" />
          Saisie manuelle d'écriture
        </h1>
        <p className="text-sm text-gray-600 mt-1">Saisir une écriture comptable dans n'importe quel journal.</p>
      </div>

      <Card>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <Label className="text-xs text-gray-500">Date</Label>
              <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Journal</Label>
              <Select value={journalCode} onChange={(e) => setJournalCode(e.target.value)}>
                {JOURNALS.map((j) => <option key={j.value} value={j.value}>{j.label}</option>)}
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-gray-500">Libellé général</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Libellé de l'écriture" required />
            </div>
          </div>

          <div className="overflow-x-auto mb-4">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Compte</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Libellé</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Débit</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Crédit</th>
                  <th className="px-3 py-2 text-center text-xs text-gray-500 uppercase">Tiers</th>
                  <th className="px-3 py-2 text-center text-xs text-gray-500 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lines.map((l, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <Select value={l.accountCode} onChange={(e) => updateLine(i, 'accountCode', e.target.value)} className="w-40">
                        <option value="">-- Compte --</option>
                        {accounts.map((a) => (
                          <option key={a.code} value={a.code}>{a.code} - {a.label}</option>
                        ))}
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <Input value={l.label} onChange={(e) => updateLine(i, 'label', e.target.value)} placeholder="Libellé ligne" />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.001" value={l.debit} onChange={(e) => updateLine(i, 'debit', e.target.value)} className="w-32 text-right" />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.001" value={l.credit} onChange={(e) => updateLine(i, 'credit', e.target.value)} className="w-32 text-right" />
                    </td>
                    <td className="px-3 py-2">
                      <Select value={l.thirdPartyType} onChange={(e) => updateLine(i, 'thirdPartyType', e.target.value)} className="w-32">
                        <option value="NONE">--</option>
                        <option value="CLIENT">Client</option>
                        <option value="SUPPLIER">Fournisseur</option>
                      </Select>
                      {l.thirdPartyType === 'CLIENT' && (
                        <Select value={l.thirdPartyId} onChange={(e) => updateLine(i, 'thirdPartyId', e.target.value)} className="w-32 mt-1">
                          <option value="">-- Client --</option>
                          {clients.map((c) => <option key={c._id} value={c._id}>{c.name || c.companyName}</option>)}
                        </Select>
                      )}
                      {l.thirdPartyType === 'SUPPLIER' && (
                        <Select value={l.thirdPartyId} onChange={(e) => updateLine(i, 'thirdPartyId', e.target.value)} className="w-32 mt-1">
                          <option value="">-- Fournisseur --</option>
                          {suppliers.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                        </Select>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button type="button" onClick={() => removeLine(i)} className="text-red-500 hover:text-red-700">
                        <Icon name="IconTrash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold bg-gray-100">
                  <td className="px-3 py-2" colSpan={2}>TOTAUX</td>
                  <td className="px-3 py-2 text-right">{totals.debit.toFixed(3)}</td>
                  <td className="px-3 py-2 text-right">{totals.credit.toFixed(3)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={balanced ? 'text-green-600' : 'text-red-600'}>
                      {balanced ? 'Équilibré' : `Diff: ${(totals.debit - totals.credit).toFixed(3)}`}
                    </span>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex justify-between items-center">
            <Button type="button" onClick={addLine} variant="secondary">
              <Icon name="IconPlus" className="mr-1" /> Ajouter ligne
            </Button>
            <Button type="submit" disabled={saving || !balanced}>
              {saving ? 'Enregistrement...' : 'Enregistrer l\'écriture'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
